import React,{ useState, useEffect, useRef, useCallback } from 'react';
import { deviceController } from '../../controllers/deviceController';
import { customerController, calculateEMI, generateSchedule } from '../../controllers/customerController';
import Device, { EMIPlan, DeviceOwner } from '../../models/Device';
import {
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle,
  Smartphone, Tablet, Monitor, Shield,
  ChevronUp, ChevronDown, CheckCircle, Battery,
  User, FileText, Calendar, TrendingUp, CheckSquare, AlertTriangle,
  Upload, Image, ChevronRight, ChevronLeft, RotateCcw, Save,
  IndianRupee, XCircle, Pen,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:    { padding: 0, fontFamily: "'DM Sans', sans-serif" },
  card:    { background: '#fff', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'hidden' },
  modal:   { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  mBox:    { background: '#fff', borderRadius: 16, width: '100%', maxHeight: '94vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.25)' },
  mHead:   { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  mFoot:   { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, background: '#fafafa' },
  mBody:   { padding: 24, overflowY: 'auto', flex: 1 },
  mTitle:  { fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 },
  btnPri:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSec:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnDng:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGrn:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  iconBtn: { padding: 6, background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  label:   { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.04em' },
  input:   { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', color: '#1e293b', background: '#fff', boxSizing: 'border-box' },
  th:      { background: '#f8fafc', padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.05em' },
  td:      { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  badge:   (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg }),
  section: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 14, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 },
  divider: { height: 1, background: '#f1f5f9', margin: '20px 0' },
  errBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16 },
  infoBox: (color) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: 10, padding: '14px 16px', marginBottom: 16 }),
  grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  progBar: (pct, color) => ({
    outer: { height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginTop: 4 },
    inner: { height: '100%', width: `${Math.min(100, pct)}%`, background: color, borderRadius: 3, transition: 'width .4s' },
  }),
  fileUp:  { border: '2px dashed #d1d5db', borderRadius: 10, padding: '18px 12px', textAlign: 'center', cursor: 'pointer' },
  tab:     (active) => ({ padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: active ? 700 : 500, cursor: 'pointer', border: 'none', background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : '#64748b', transition: 'all .15s' }),
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const PlatformIcon = ({ platform, size = 16 }) => {
  if (platform === 'iOS') return <Tablet size={size} />;
  if (platform === 'Windows' || platform === 'macOS') return <Monitor size={size} />;
  return <Smartphone size={size} />;
};

const Badge = ({ color, bg, children }) => (
  <span style={S.badge(color, bg)}>{children}</span>
);

const ErrList = ({ errors }) =>
  errors?.length ? (
    <div style={S.errBox}>
      {errors.map((e, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626', fontSize: 13, marginBottom: i < errors.length - 1 ? 4 : 0 }}>
          <AlertCircle size={13} /> {e}
        </div>
      ))}
    </div>
  ) : null;

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const paymentStatusColor = (s) =>
  ({ Paid: { color: '#16a34a', bg: '#dcfce7' }, Pending: { color: '#2563eb', bg: '#dbeafe' }, Overdue: { color: '#dc2626', bg: '#fee2e2' }, Partial: { color: '#d97706', bg: '#fef3c7' } }[s] || { color: '#64748b', bg: '#f3f4f6' });

// ─────────────────────────────────────────────────────────────────────────────
// SIGNATURE PAD
// ─────────────────────────────────────────────────────────────────────────────
const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSign, setHasSign] = useState(!!value);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e, canvasRef.current); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => { e.preventDefault(); if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = getPos(e, canvasRef.current); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.stroke(); };
  const stop = () => { drawing.current = false; const data = canvasRef.current.toDataURL(); setHasSign(true); onChange(data); };
  const clear = () => { canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); setHasSign(false); onChange(null); };

  useEffect(() => {
    if (value && canvasRef.current && !hasSign) {
      const img = new window.Image();
      img.onload = () => canvasRef.current.getContext('2d').drawImage(img, 0, 0);
      img.src = value;
    }
  }, []); // eslint-disable-line

  return (
    <div>
      <div style={{ border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', background: '#fafafa' }}>
        <canvas ref={canvasRef} width={400} height={120} style={{ display: 'block', width: '100%', touchAction: 'none', cursor: 'crosshair' }}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
        <button type="button" style={S.btnSec} onClick={clear}><RotateCcw size={13} /> Clear</button>
        {hasSign ? <span style={{ fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={13} /> Signature captured</span>
          : <span style={{ fontSize: 12, color: '#94a3b8' }}>Draw signature above</span>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FILE FIELD
// ─────────────────────────────────────────────────────────────────────────────
const FileField = ({ label, value, onChange, accept = 'image/*' }) => {
  const ref = useRef();
  const isImg = value && (value.startsWith('data:image') || value.startsWith('http'));
  return (
    <div>
      <label style={S.label}>{label}</label>
      <div style={{ ...S.fileUp, borderColor: value ? '#2563eb' : '#d1d5db', background: value ? '#eff6ff' : '#fff' }} onClick={() => ref.current.click()}>
        {isImg ? <img src={value} alt={label} style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 6, objectFit: 'contain' }} />
          : value ? <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 600 }}>✓ {label} uploaded</div>
          : <div style={{ fontSize: 13, color: '#94a3b8' }}><Upload size={16} style={{ marginBottom: 4 }} /><br />Click to upload {label}</div>}
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => onChange(r.result, f); r.readAsDataURL(f); }} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EMI SECTION
// ─────────────────────────────────────────────────────────────────────────────
const EMISection = ({ emiPlan, onChange }) => {
  const defaultPlan = {
    productName: '', productPrice: 0, downPayment: 0, interestRate: 0,
    tenure: 12, tenureType: 'monthly', customDays: 30,
    startDate: new Date().toISOString().split('T')[0],
    emiAmount: 0, totalPayable: 0, totalInterest: 0, payments: [], status: 'Active',
  };
  const [plan, setPlan] = useState(() => emiPlan ? { ...defaultPlan, ...emiPlan } : { ...defaultPlan });
  const [calculated, setCalculated] = useState(false);

  const update = (field, val) => {
    const p = { ...plan, [field]: val };
    setPlan(p);
    setCalculated(false);
    onChange(p);
  };

  const calculate = () => {
    const result = generateSchedule(plan);
    const updated = { ...plan, ...result };
    setPlan(updated);
    setCalculated(true);
    onChange(updated);
  };

  const tenureLabel = { weekly: 'Weeks', monthly: 'Months', yearly: 'Years', custom: 'Periods' }[plan.tenureType] || 'Months';

  return (
    <div>
      <div style={S.grid2}>
        <div>
          <label style={S.label}>Product / Item Name</label>
          <input style={S.input} value={plan.productName} onChange={e => update('productName', e.target.value)} placeholder="e.g. Samsung Galaxy S24" />
        </div>
        <div>
          <label style={S.label}>Product Price (₹)</label>
          <input type="number" style={S.input} value={plan.productPrice || ''} onChange={e => update('productPrice', +e.target.value)} placeholder="0" />
        </div>
        <div>
          <label style={S.label}>Down Payment (₹)</label>
          <input type="number" style={S.input} value={plan.downPayment || ''} onChange={e => update('downPayment', +e.target.value)} placeholder="0" />
        </div>
        <div>
          <label style={S.label}>Interest Rate (% per annum)</label>
          <input type="number" style={S.input} value={plan.interestRate || ''} onChange={e => update('interestRate', +e.target.value)} placeholder="0" step="0.1" />
        </div>
        <div>
          <label style={S.label}>Tenure Type</label>
          <select style={S.input} value={plan.tenureType} onChange={e => update('tenureType', e.target.value)}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom Days</option>
          </select>
        </div>
        <div>
          <label style={S.label}>Tenure ({tenureLabel})</label>
          <input type="number" style={S.input} value={plan.tenure || ''} onChange={e => update('tenure', +e.target.value)} min="1" placeholder="12" />
        </div>
        {plan.tenureType === 'custom' && (
          <div>
            <label style={S.label}>Custom Interval (Days)</label>
            <input type="number" style={S.input} value={plan.customDays || ''} onChange={e => update('customDays', +e.target.value)} min="1" placeholder="30" />
          </div>
        )}
        <div>
          <label style={S.label}>Start Date</label>
          <input type="date" style={S.input} value={plan.startDate} onChange={e => update('startDate', e.target.value)} />
        </div>
      </div>

      <button type="button" style={{ ...S.btnPri, marginTop: 14, width: '100%', justifyContent: 'center' }} onClick={calculate}>
        <TrendingUp size={15} /> Calculate EMI & Generate Schedule
      </button>

      {calculated && plan.emiAmount > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
            {[
              ['EMI Amount', fmt(plan.emiAmount), '#2563eb', '#dbeafe'],
              ['Total Payable', fmt(plan.totalPayable), '#0f172a', '#f1f5f9'],
              ['Total Interest', fmt(plan.totalInterest), '#dc2626', '#fee2e2'],
              ['Down Payment', fmt(plan.downPayment), '#16a34a', '#dcfce7'],
            ].map(([label, val, color, bg]) => (
              <div key={label} style={{ background: bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>{['#', 'Due Date', 'EMI', 'Principal', 'Interest', 'Balance', 'Status'].map(h => (
                  <th key={h} style={{ background: '#f8fafc', padding: '9px 10px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(plan.payments || []).map((p, i) => {
                  const sc = paymentStatusColor(p.status);
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>{p.installmentNo}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{p.dueDate}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>{fmt(p.emiAmount)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#16a34a' }}>{fmt(p.principal)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#dc2626' }}>{fmt(p.interest)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>{fmt(p.balance)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={S.badge(sc.color, sc.bg)}>{p.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER FORM MODAL  (Create / Edit)
// ─────────────────────────────────────────────────────────────────────────────
const CUST_TABS = ['Personal Info', 'Documents & Sign', 'EMI Plan'];

const emptyCustomer = () => ({
  fullName: '', phoneNumber: '', alternatePhone: '', email: '',
  address: '', city: '', state: '', pincode: '',
  aadharNumber: '', panNumber: '',
  imei1: '', imei2: '',
  profileImage: null, aadharCard: null, panCard: null,
  digitalSignature: null, signedAt: null,
  notes: '',
  emiPlan: null,
});

const CustomerFormModal = ({ mode, customer, onClose, onSaved, submitting, setSubmitting }) => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(() => customer ? { ...emptyCustomer(), ...customer } : emptyCustomer());
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState([]);
  const [emiEnabled, setEmiEnabled] = useState(!!(customer?.emiPlan));

  const set = (field, val) => setData(d => ({ ...d, [field]: val }));

  const inp = (field) => ({
    style: S.input,
    value: data[field] || '',
    onChange: e => set(field, e.target.value),
  });

  const handleFileChange = (key, dataUrl, file) => {
    set(key, dataUrl);
    setFiles(f => ({ ...f, [key]: file }));
  };

  const validate = () => {
    const errs = [];
    if (!data.fullName?.trim()) errs.push('Full name is required');
    if (!data.phoneNumber?.trim()) errs.push('Phone number is required');
    if (data.phoneNumber && !/^\d{10}$/.test(data.phoneNumber.replace(/\D/g, ''))) errs.push('Phone number must be 10 digits');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (errs.length) { setErrors(errs); setTab(0); return; }
    setErrors([]);
    setSubmitting(true);
    try {
      const payload = { ...data, emiPlan: emiEnabled ? data.emiPlan : null };
      let r;
      if (mode === 'add') {
        r = await customerController.create(payload, files);
      } else {
        r = await customerController.update(customer._id || customer.id, payload, files);
      }
      if (r.success) {
        onSaved(r.data, mode);
      } else {
        setErrors(r.errors?.length ? r.errors : [r.message || 'Something went wrong']);
      }
    } catch (e) {
      setErrors([e.message || 'Unexpected error']);
    }
    setSubmitting(false);
  };

  return (
    <div style={S.modal} onClick={!submitting ? onClose : undefined}>
      <div style={{ ...S.mBox, maxWidth: 820 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <div>
            <h2 style={S.mTitle}>{mode === 'add' ? 'Add New Customer' : `Edit — ${customer?.fullName}`}</h2>
            {data.customerKey && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontFamily: 'monospace' }}>Key: {data.customerKey}</div>}
          </div>
          {!submitting && <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>}
        </div>

        {/* Tab bar */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 4, background: '#fafafa', flexShrink: 0 }}>
          {CUST_TABS.map((t, i) => (
            <button key={t} style={S.tab(tab === i)} onClick={() => setTab(i)}>{t}</button>
          ))}
        </div>

        <div style={S.mBody}>
          <ErrList errors={errors} />

          {/* ── TAB 0: PERSONAL INFO ── */}
          {tab === 0 && (
            <div>
              <div style={S.section}><User size={13} /> Personal Information</div>
              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Full Name *</label>
                  <input {...inp('fullName')} placeholder="Customer full name" />
                </div>
                <div>
                  <label style={S.label}>Phone Number *</label>
                  <input {...inp('phoneNumber')} placeholder="10-digit mobile" maxLength={10} />
                </div>
                <div>
                  <label style={S.label}>Alternate Phone</label>
                  <input {...inp('alternatePhone')} placeholder="Alternate mobile" maxLength={10} />
                </div>
                <div>
                  <label style={S.label}>Email</label>
                  <input {...inp('email')} type="email" placeholder="email@example.com" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={S.label}>Address</label>
                  <input {...inp('address')} placeholder="Full address" />
                </div>
                <div>
                  <label style={S.label}>City</label>
                  <input {...inp('city')} placeholder="City" />
                </div>
                <div>
                  <label style={S.label}>State</label>
                  <input {...inp('state')} placeholder="State" />
                </div>
                <div>
                  <label style={S.label}>Pincode</label>
                  <input {...inp('pincode')} placeholder="6-digit pincode" maxLength={6} />
                </div>
              </div>

              <div style={S.divider} />
              <div style={S.section}><FileText size={13} /> Document Numbers</div>
              <div style={S.grid2}>
                <div>
                  <label style={S.label}>Aadhar Number</label>
                  <input {...inp('aadharNumber')} placeholder="12-digit Aadhar" maxLength={12} />
                </div>
                <div>
                  <label style={S.label}>PAN Number</label>
                  <input {...inp('panNumber')} placeholder="ABCDE1234F" maxLength={10} style={{ ...S.input, textTransform: 'uppercase' }} />
                </div>
              </div>

              <div style={S.divider} />
              <div style={S.section}><Smartphone size={13} /> Device IMEI (Optional)</div>
              <div style={S.grid2}>
                <div>
                  <label style={S.label}>IMEI 1</label>
                  <input {...inp('imei1')} placeholder="15-digit IMEI" maxLength={15} />
                </div>
                <div>
                  <label style={S.label}>IMEI 2 (Dual SIM)</label>
                  <input {...inp('imei2')} placeholder="15-digit IMEI" maxLength={15} />
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <label style={S.label}>Notes</label>
                <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                  value={data.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Additional notes..." />
              </div>
            </div>
          )}

          {/* ── TAB 1: DOCUMENTS & SIGNATURE ── */}
          {tab === 1 && (
            <div>
              <div style={S.section}><Image size={13} /> Owner Documents</div>
              <div style={S.grid3}>
                <FileField label="Profile Photo" value={data.profileImage}
                  onChange={(v, f) => handleFileChange('profileImage', v, f)} accept="image/*" />
                <FileField label="Aadhar Card" value={data.aadharCard}
                  onChange={(v, f) => handleFileChange('aadharCard', v, f)} accept="image/*,application/pdf" />
                <FileField label="PAN Card" value={data.panCard}
                  onChange={(v, f) => handleFileChange('panCard', v, f)} accept="image/*,application/pdf" />
              </div>

              <div style={S.divider} />
              <div style={S.section}><Pen size={13} /> Digital Signature</div>
              <div style={{ maxWidth: 420 }}>
                <SignaturePad
                  value={data.digitalSignature}
                  onChange={v => { set('digitalSignature', v); if (v) set('signedAt', new Date().toISOString()); }}
                />
                {data.signedAt && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                    Signed at: {new Date(data.signedAt).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 2: EMI PLAN ── */}
          {tab === 2 && (
            <div>
              <div style={S.section}><IndianRupee size={13} /> EMI / Payment Plan</div>

              {/* Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  <input type="checkbox" checked={emiEnabled} onChange={e => setEmiEnabled(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#2563eb', cursor: 'pointer' }} />
                  Enable EMI Plan for this customer
                </label>
                {emiEnabled && <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 'auto' }}>✓ EMI Enabled</span>}
              </div>

              {emiEnabled ? (
                <>
                  <div style={{ ...S.infoBox('#2563eb'), fontSize: 13 }}>
                    💡 Fill in product details and click "Calculate EMI" to generate the full payment schedule.
                  </div>
                  <EMISection
                    emiPlan={data.emiPlan}
                    onChange={plan => set('emiPlan', plan)}
                  />
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 24px', color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 12 }}>
                  <IndianRupee size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
                  <div style={{ fontSize: 14 }}>Enable EMI plan above to configure payment schedule</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={S.mFoot}>
          {tab > 0 && <button style={S.btnSec} onClick={() => setTab(t => t - 1)}><ChevronLeft size={14} /> Back</button>}
          <div style={{ flex: 1 }} />
          {!submitting && <button style={S.btnSec} onClick={onClose}>Cancel</button>}
          {tab < CUST_TABS.length - 1
            ? <button style={S.btnPri} onClick={() => setTab(t => t + 1)}>Next <ChevronRight size={14} /></button>
            : <button style={S.btnPri} onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Saving...' : mode === 'add' ? 'Create Customer' : 'Save Changes'}
              </button>}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENT HISTORY MODAL
// ─────────────────────────────────────────────────────────────────────────────
const PaymentHistoryModal = ({ device, onClose, onUpdate }) => {
  const [payments, setPayments] = useState(device?.emiPlan?.payments || []);
  const [updating, setUpdating] = useState(null);
  const [payForm, setPayForm] = useState({ paidAmount: '', paidDate: new Date().toISOString().split('T')[0], notes: '' });
  const plan = device?.emiPlan;
  if (!plan) return null;

  const totalPaid = payments.reduce((s, p) => s + (p.paidAmount || 0), 0);
  const totalPayable = plan.totalPayable || 0;
  const progress = totalPayable ? Math.round((totalPaid / totalPayable) * 100) : 0;

  const markPayment = (idx) => {
    const updated = payments.map((p, i) => {
      if (i !== idx) return p;
      const paid = +payForm.paidAmount || p.emiAmount;
      return { ...p, paidAmount: paid, paidDate: payForm.paidDate, notes: payForm.notes, status: paid >= p.emiAmount ? 'Paid' : 'Partial' };
    });
    setPayments(updated); setUpdating(null); onUpdate(updated);
  };

  const markOverdue = (idx) => setPayments(prev => prev.map((p, i) => i === idx ? { ...p, status: 'Overdue' } : p));

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mBox, maxWidth: 920 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <div>
            <h2 style={S.mTitle}>Payment History — {device.deviceName || device.fullName}</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {device.deviceKey || device.customerKey} · {plan.productName} · {fmt(plan.productPrice)}
            </div>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={S.mBody}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 20 }}>
            {[
              ['Total Payable', fmt(totalPayable), '#0f172a', '#f1f5f9'],
              ['Total Paid', fmt(totalPaid), '#16a34a', '#dcfce7'],
              ['Remaining', fmt(totalPayable - totalPaid), '#2563eb', '#dbeafe'],
              ['EMI Amount', fmt(plan.emiAmount), '#7c3aed', '#ede9fe'],
              ['Progress', `${progress}%`, '#ea580c', '#ffedd5'],
            ].map(([label, val, color, bg]) => (
              <div key={label} style={{ background: bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={S.progBar(progress, '#2563eb').outer}><div style={S.progBar(progress, '#2563eb').inner} /></div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 20, marginTop: 4 }}>
            {payments.filter(p => p.status === 'Paid').length} of {payments.length} installments paid
          </div>

          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>{['#', 'Due Date', 'EMI', 'Paid Amount', 'Paid Date', 'Status', 'Notes', 'Action'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {payments.map((p, i) => {
                  const sc = paymentStatusColor(p.status);
                  const isUpd = updating === i;
                  return (
                    <React.Fragment key={i}>
                      <tr style={{ background: p.status === 'Paid' ? '#f0fdf4' : p.status === 'Overdue' ? '#fff5f5' : '#fff' }}>
                        <td style={S.td}>{p.installmentNo}</td>
                        <td style={S.td}>{p.dueDate}</td>
                        <td style={{ ...S.td, fontWeight: 700 }}>{fmt(p.emiAmount)}</td>
                        <td style={{ ...S.td, color: p.paidAmount ? '#16a34a' : '#94a3b8' }}>{p.paidAmount ? fmt(p.paidAmount) : '—'}</td>
                        <td style={{ ...S.td, fontSize: 12, color: '#64748b' }}>{p.paidDate || '—'}</td>
                        <td style={S.td}><span style={S.badge(sc.color, sc.bg)}>{p.status}</span></td>
                        <td style={{ ...S.td, fontSize: 12, color: '#64748b', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes || '—'}</td>
                        <td style={S.td}>
                          {p.status !== 'Paid' ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button style={{ ...S.btnGrn, padding: '5px 10px', fontSize: 11 }} onClick={() => setUpdating(isUpd ? null : i)}>
                                <CheckSquare size={12} /> Mark Paid
                              </button>
                              {p.status !== 'Overdue' && (
                                <button style={{ ...S.btnDng, padding: '5px 10px', fontSize: 11 }} onClick={() => markOverdue(i)}>
                                  <AlertTriangle size={12} />
                                </button>
                              )}
                            </div>
                          ) : <span style={{ color: '#16a34a', fontSize: 12 }}>✓ Done</span>}
                        </td>
                      </tr>
                      {isUpd && (
                        <tr style={{ background: '#f0f9ff' }}>
                          <td colSpan={8} style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                              <div>
                                <label style={S.label}>Paid Amount (₹)</label>
                                <input type="number" style={{ ...S.input, width: 140 }} value={payForm.paidAmount} placeholder={p.emiAmount}
                                  onChange={e => setPayForm(f => ({ ...f, paidAmount: e.target.value }))} />
                              </div>
                              <div>
                                <label style={S.label}>Payment Date</label>
                                <input type="date" style={{ ...S.input, width: 160 }} value={payForm.paidDate}
                                  onChange={e => setPayForm(f => ({ ...f, paidDate: e.target.value }))} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={S.label}>Notes</label>
                                <input style={S.input} value={payForm.notes} placeholder="Payment notes..."
                                  onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                              </div>
                              <button style={S.btnGrn} onClick={() => markPayment(i)}><Save size={14} /> Save</button>
                              <button style={S.btnSec} onClick={() => setUpdating(null)}><X size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div style={S.mFoot}><button style={S.btnSec} onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DEVICE FORM MODAL  (unchanged — kept for device enrollment)
// ─────────────────────────────────────────────────────────────────────────────
const DEV_TABS = ['Owner', 'Device', 'Documents & Sign', 'EMI Plan'];

const DeviceFormModal = ({ mode, device, onClose, onSave, submitting }) => {
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(() => device ? device.toJSON() : new Device().toJSON());
  const [errors, setErrors] = useState([]);

  const setOwner = (field, val) => setData(d => ({ ...d, owner: { ...d.owner, [field]: val } }));
  const setField = (field, val) => setData(d => ({ ...d, [field]: val }));
  const setEmi = (plan) => setData(d => ({ ...d, emiPlan: plan ? { ...plan } : null }));

  const inp = (field, ownerField = false) => ({
    style: S.input,
    value: ownerField ? (data.owner?.[field] || '') : (data[field] || ''),
    onChange: e => ownerField ? setOwner(field, e.target.value) : setField(field, e.target.value),
  });

  const handleSubmit = async () => {
    setErrors([]);
    const dev = new Device(data);
    const val = dev.validate();
    if (!val.isValid) { setErrors(val.errors); setTab(0); return; }
    await onSave(data);
  };

  return (
    <div style={S.modal} onClick={!submitting ? onClose : undefined}>
      <div style={{ ...S.mBox, maxWidth: 800 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <div>
            <h2 style={S.mTitle}>{mode === 'add' ? 'Enroll New Device' : `Edit — ${device?.deviceName}`}</h2>
            {data.deviceKey && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontFamily: 'monospace' }}>Key: {data.deviceKey}</div>}
          </div>
          {!submitting && <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>}
        </div>

        <div style={{ padding: '10px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 4, background: '#fafafa', flexShrink: 0 }}>
          {DEV_TABS.map((t, i) => <button key={t} style={S.tab(tab === i)} onClick={() => setTab(i)}>{t}</button>)}
        </div>

        <div style={S.mBody}>
          <ErrList errors={errors} />

          {tab === 0 && (
            <div>
              <div style={S.section}><User size={13} /> Owner Information</div>
              <div style={S.grid2}>
                <div><label style={S.label}>Full Name *</label><input {...inp('fullName', true)} placeholder="Owner full name" /></div>
                <div><label style={S.label}>Phone Number *</label><input {...inp('phoneNumber', true)} placeholder="10-digit mobile" maxLength={10} /></div>
                <div><label style={S.label}>Alternate Phone</label><input {...inp('alternatePhone', true)} placeholder="Alternate mobile" maxLength={10} /></div>
                <div><label style={S.label}>Email</label><input {...inp('email', true)} type="email" placeholder="email@example.com" /></div>
                <div style={{ gridColumn: '1/-1' }}><label style={S.label}>Address</label><input {...inp('address', true)} placeholder="Full address" /></div>
                <div><label style={S.label}>City</label><input {...inp('city', true)} placeholder="City" /></div>
                <div><label style={S.label}>State</label><input {...inp('state', true)} placeholder="State" /></div>
                <div><label style={S.label}>Pincode</label><input {...inp('pincode', true)} placeholder="6-digit pincode" maxLength={6} /></div>
              </div>
              <div style={S.divider} />
              <div style={S.section}><FileText size={13} /> Document Numbers</div>
              <div style={S.grid2}>
                <div><label style={S.label}>Aadhar Number</label><input {...inp('aadharNumber', true)} placeholder="12-digit Aadhar" maxLength={12} /></div>
                <div><label style={S.label}>PAN Number</label><input {...inp('panNumber', true)} placeholder="ABCDE1234F" maxLength={10} style={{ ...S.input, textTransform: 'uppercase' }} /></div>
              </div>
            </div>
          )}

          {tab === 1 && (
            <div>
              <div style={S.section}><Smartphone size={13} /> Device Details</div>
              <div style={S.grid2}>
                <div><label style={S.label}>Device Name *</label><input {...inp('deviceName')} placeholder="e.g. Samsung Galaxy S24" /></div>
                <div><label style={S.label}>Serial Number *</label><input {...inp('serialNumber')} placeholder="Serial number" /></div>
                <div>
                  <label style={S.label}>Platform *</label>
                  <select style={S.input} value={data.platform} onChange={e => setField('platform', e.target.value)}>
                    {['Android', 'iOS', 'Windows', 'macOS'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div><label style={S.label}>OS Version</label><input {...inp('osVersion')} placeholder="e.g. Android 14" /></div>
                <div><label style={S.label}>Model *</label><input {...inp('model')} placeholder="e.g. SM-S928B" /></div>
                <div><label style={S.label}>Manufacturer</label><input {...inp('manufacturer')} placeholder="e.g. Samsung" /></div>
                <div><label style={S.label}>IMEI 1</label><input {...inp('imei1')} placeholder="15-digit IMEI" maxLength={15} /></div>
                <div><label style={S.label}>IMEI 2</label><input {...inp('imei2')} placeholder="15-digit IMEI" maxLength={15} /></div>
                <div><label style={S.label}>MAC Address</label><input {...inp('macAddress')} placeholder="xx:xx:xx:xx:xx:xx" /></div>
                <div><label style={S.label}>IP Address</label><input {...inp('ipAddress')} placeholder="192.168.x.x" /></div>
              </div>
              <div style={S.divider} />
              <div style={S.section}><Shield size={13} /> Assignment & Status</div>
              <div style={S.grid2}>
                <div><label style={S.label}>Assigned To</label><input {...inp('assignedTo')} placeholder="Employee name" /></div>
                <div><label style={S.label}>Department</label><input {...inp('department')} placeholder="e.g. Sales" /></div>
                <div><label style={S.label}>Group</label><input {...inp('group')} placeholder="e.g. Field Team" /></div>
                <div><label style={S.label}>Policy Name</label><input {...inp('policyName')} placeholder="e.g. Corporate Policy" /></div>
                <div>
                  <label style={S.label}>Status</label>
                  <select style={S.input} value={data.status} onChange={e => setField('status', e.target.value)}>
                    {['Pending', 'Active', 'Inactive', 'Lost', 'Retired'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Compliance</label>
                  <select style={S.input} value={data.complianceStatus} onChange={e => setField('complianceStatus', e.target.value)}>
                    {['Compliant', 'Non-Compliant', 'Unknown'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><label style={S.label}>Battery Level (%)</label><input type="number" style={S.input} value={data.batteryLevel} onChange={e => setField('batteryLevel', +e.target.value)} min={0} max={100} /></div>
                <div><label style={S.label}>Storage Used (GB)</label><input type="number" style={S.input} value={data.storageUsed} onChange={e => setField('storageUsed', +e.target.value)} min={0} /></div>
                <div><label style={S.label}>Storage Total (GB)</label><input type="number" style={S.input} value={data.storageTotal} onChange={e => setField('storageTotal', +e.target.value)} min={1} /></div>
                <div><label style={S.label}>Location</label><input {...inp('location')} placeholder="e.g. Delhi Office" /></div>
              </div>
              <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
                {[['isEncrypted', 'Encryption Enabled', '#2563eb'], ['isLocked', 'Device Locked', '#dc2626']].map(([key, label, color]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    <input type="checkbox" checked={!!data[key]} onChange={e => setField(key, e.target.checked)} style={{ width: 16, height: 16, accentColor: color, cursor: 'pointer' }} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 14 }}>
                <label style={S.label}>Notes</label>
                <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                  value={data.notes} onChange={e => setField('notes', e.target.value)} placeholder="Additional notes..." />
              </div>
            </div>
          )}

          {tab === 2 && (
            <div>
              <div style={S.section}><Image size={13} /> Owner Documents</div>
              <div style={S.grid3}>
                <FileField label="Profile Photo" value={data.owner?.profileImage} onChange={v => setOwner('profileImage', v)} accept="image/*" />
                <FileField label="Aadhar Card" value={data.owner?.aadharCard} onChange={v => setOwner('aadharCard', v)} accept="image/*,application/pdf" />
                <FileField label="PAN Card" value={data.owner?.panCard} onChange={v => setOwner('panCard', v)} accept="image/*,application/pdf" />
              </div>
              <div style={S.divider} />
              <div style={S.section}><Pen size={13} /> Digital Signature</div>
              <div style={{ maxWidth: 420 }}>
                <SignaturePad value={data.owner?.digitalSignature}
                  onChange={v => { setOwner('digitalSignature', v); if (v) setOwner('signedAt', new Date().toISOString()); }} />
                {data.owner?.signedAt && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Signed at: {new Date(data.owner.signedAt).toLocaleString('en-IN')}</div>}
              </div>
            </div>
          )}

          {tab === 3 && (
            <div>
              <div style={S.section}><IndianRupee size={13} /> EMI / Payment Plan</div>
              <div style={{ ...S.infoBox('#2563eb'), fontSize: 13 }}>
                💡 Fill in product details and click "Calculate EMI" to generate the full payment schedule.
              </div>
              <EMISection emiPlan={data.emiPlan} onChange={setEmi} />
            </div>
          )}
        </div>

        <div style={S.mFoot}>
          {tab > 0 && <button style={S.btnSec} onClick={() => setTab(t => t - 1)}><ChevronLeft size={14} /> Back</button>}
          <div style={{ flex: 1 }} />
          {!submitting && <button style={S.btnSec} onClick={onClose}>Cancel</button>}
          {tab < DEV_TABS.length - 1
            ? <button style={S.btnPri} onClick={() => setTab(t => t + 1)}>Next <ChevronRight size={14} /></button>
            : <button style={S.btnPri} onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Saving...' : mode === 'add' ? 'Enroll Device' : 'Save Changes'}
              </button>}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VIEW MODAL — Device + Customer data combined
// ─────────────────────────────────────────────────────────────────────────────
const ViewModal = ({ device: dev, onClose, onEdit, onPayments }) => {
  const [vtab, setVtab] = useState(0);
  const VTABS = ['Overview', 'Owner & Docs', 'EMI Summary'];
  const plan = dev.emiPlan;

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mBox, maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div style={S.mHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${dev.getPlatformColor()}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: dev.getPlatformColor(), flexShrink: 0 }}>
              <PlatformIcon platform={dev.platform} size={22} />
            </div>
            <div>
              <h2 style={{ ...S.mTitle, marginBottom: 2 }}>{dev.deviceName}</h2>
              <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{dev.deviceKey}</div>
            </div>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={{ padding: '10px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 4, background: '#fafafa', flexShrink: 0 }}>
          {VTABS.map((t, i) => <button key={t} style={S.tab(vtab === i)} onClick={() => setVtab(i)}>{t}</button>)}
        </div>

        <div style={S.mBody}>
          {vtab === 0 && (
            <div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                <Badge color={dev.getStatusColor()} bg={`${dev.getStatusColor()}18`}>{dev.status}</Badge>
                <Badge color={dev.getComplianceColor()} bg={`${dev.getComplianceColor()}18`}>{dev.complianceStatus}</Badge>
                {dev.isEncrypted && <Badge color="#2563eb" bg="#dbeafe">🔒 Encrypted</Badge>}
                {dev.isLocked && <Badge color="#dc2626" bg="#fee2e2">🔐 Locked</Badge>}
                {dev.isEnrolled && <Badge color="#16a34a" bg="#dcfce7">✓ Enrolled</Badge>}
              </div>
              <div style={S.grid2}>
                {[
                  ['Platform', `${dev.platform} ${dev.osVersion}`],
                  ['Model', dev.model],
                  ['Manufacturer', dev.manufacturer || '—'],
                  ['Serial No.', dev.serialNumber],
                  ['IMEI 1', dev.imei1 || '—'],
                  ['IMEI 2', dev.imei2 || '—'],
                  ['MAC Address', dev.macAddress || '—'],
                  ['IP Address', dev.ipAddress || '—'],
                  ['Assigned To', dev.assignedTo || '—'],
                  ['Department', dev.department || '—'],
                  ['Group', dev.group || '—'],
                  ['Policy', dev.policyName || '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a' }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={S.divider} />
              <div style={S.section}><Battery size={13} /> Hardware</div>
              <div style={S.grid2}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>BATTERY</div>
                  <div style={{ fontWeight: 700, color: dev.batteryLevel < 20 ? '#dc2626' : '#16a34a' }}>{dev.batteryLevel}%</div>
                  <div style={S.progBar(dev.batteryLevel, dev.batteryLevel < 20 ? '#dc2626' : '#16a34a').outer}>
                    <div style={S.progBar(dev.batteryLevel, dev.batteryLevel < 20 ? '#dc2626' : '#16a34a').inner} />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>STORAGE</div>
                  <div style={{ fontWeight: 700 }}>{dev.storageUsed} / {dev.storageTotal} GB ({dev.getStoragePercent()}%)</div>
                  <div style={S.progBar(dev.getStoragePercent(), dev.getStoragePercent() > 85 ? '#dc2626' : '#2563eb').outer}>
                    <div style={S.progBar(dev.getStoragePercent(), dev.getStoragePercent() > 85 ? '#dc2626' : '#2563eb').inner} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {vtab === 1 && (
            <div>
              <div style={S.section}><User size={13} /> Owner Details</div>
              <div style={S.grid2}>
                {[
                  ['Full Name', dev.owner?.fullName || '—'],
                  ['Phone', dev.owner?.phoneNumber || '—'],
                  ['Alternate Phone', dev.owner?.alternatePhone || '—'],
                  ['Email', dev.owner?.email || '—'],
                  ['Address', dev.owner?.address || '—'],
                  ['City / State', `${dev.owner?.city || '—'} / ${dev.owner?.state || '—'}`],
                  ['Aadhar No.', dev.owner?.aadharNumber || '—'],
                  ['PAN No.', dev.owner?.panNumber || '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a' }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={S.divider} />
              <div style={S.section}><Image size={13} /> Documents</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[['Profile Photo', dev.owner?.profileImage], ['Aadhar Card', dev.owner?.aadharCard], ['PAN Card', dev.owner?.panCard]].map(([label, src]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{label}</div>
                    {src
                      ? <img src={src} alt={label} style={{ width: 100, height: 70, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                      : <div style={{ width: 100, height: 70, borderRadius: 8, border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94a3b8' }}>Not uploaded</div>}
                  </div>
                ))}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Digital Signature</div>
                  {dev.owner?.digitalSignature
                    ? <img src={dev.owner.digitalSignature} alt="Signature" style={{ width: 160, height: 70, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fafafa' }} />
                    : <div style={{ width: 160, height: 70, borderRadius: 8, border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#94a3b8' }}>Not signed</div>}
                  {dev.owner?.signedAt && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{new Date(dev.owner.signedAt).toLocaleDateString('en-IN')}</div>}
                </div>
              </div>
            </div>
          )}

          {vtab === 2 && plan && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 18 }}>
                {[
                  ['Product', plan.productName, '#0f172a', '#f1f5f9'],
                  ['Product Price', fmt(plan.productPrice), '#0f172a', '#f1f5f9'],
                  ['EMI Amount', fmt(plan.emiAmount), '#2563eb', '#dbeafe'],
                  ['Total Payable', fmt(plan.totalPayable), '#7c3aed', '#ede9fe'],
                  ['Down Payment', fmt(plan.downPayment), '#16a34a', '#dcfce7'],
                  ['Interest', fmt(plan.totalInterest), '#dc2626', '#fee2e2'],
                  ['Tenure', `${plan.tenure} ${plan.tenureType}`, '#ea580c', '#ffedd5'],
                  ['Status', plan.status, '#0f172a', '#f1f5f9'],
                ].map(([k, v, color, bg]) => (
                  <div key={k} style={{ background: bg, borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color }}>{v}</div>
                  </div>
                ))}
              </div>
              {plan.payments?.length > 0 && (
                <>
                  <div style={S.section}><Calendar size={13} /> Next Due</div>
                  {(() => {
                    const next = plan.payments?.find(p => p.status !== 'Paid');
                    return next ? (
                      <div style={{ ...S.infoBox('#dc2626'), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>Installment #{next.installmentNo}</div>
                          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Due: {next.dueDate}</div>
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>{fmt(next.emiAmount)}</div>
                      </div>
                    ) : <div style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>✓ All payments completed!</div>;
                  })()}
                </>
              )}
            </div>
          )}
          {vtab === 2 && !plan && (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No EMI plan configured for this device.</div>
          )}
        </div>

        <div style={S.mFoot}>
          {plan && plan.payments?.length > 0 && (
            <button style={S.btnGrn} onClick={() => { onClose(); onPayments(dev); }}><IndianRupee size={14} /> Payment History</button>
          )}
          <button style={S.btnSec} onClick={() => { onClose(); onEdit(dev); }}><Edit2 size={14} /> Edit</button>
          <button style={S.btnPri} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const AllDevicesPage = () => {
  const [devices, setDevices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('All');
  const [fPlat, setFPlat] = useState('All');
  const [fComp, setFComp] = useState('All');
  const [sortField, setSortF] = useState('createdAt');
  const [sortDir, setSortD] = useState('desc');
  const [page, setPage] = useState(1);
  const PER = 10;

  // Device modals
  const [showDevAdd, setShowDevAdd] = useState(false);
  const [showDevEdit, setShowDevEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showDel, setShowDel] = useState(false);
  const [showPay, setShowPay] = useState(false);

  // Customer modals
  const [showCustAdd, setShowCustAdd] = useState(false);
  const [showCustEdit, setShowCustEdit] = useState(false);

  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    const r = await deviceController.getAll();
    if (r.success) setDevices(r.data);
    else showToast('Error loading devices: ' + r.message, 'error');
    setLoading(false);
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  useEffect(() => {
    let r = [...devices];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(d => [d.deviceName, d.serialNumber, d.owner?.fullName, d.owner?.phoneNumber, d.model, d.deviceKey].some(v => v?.toLowerCase().includes(q)));
    }
    if (fStatus !== 'All') r = r.filter(d => d.status === fStatus);
    if (fPlat !== 'All') r = r.filter(d => d.platform === fPlat);
    if (fComp !== 'All') r = r.filter(d => d.complianceStatus === fComp);
    r.sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv || '') : (bv || '').localeCompare(av);
      return sortDir === 'asc' ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    setFiltered(r); setPage(1);
  }, [devices, search, fStatus, fPlat, fComp, sortField, sortDir]);

  const handleSort = (f) => { if (sortField === f) setSortD(d => d === 'asc' ? 'desc' : 'asc'); else { setSortF(f); setSortD('asc'); } };

  // ── Device save ──
  const handleDeviceSave = async (data) => {
    setSaving(true);
    let r;
    if (showDevAdd) r = await deviceController.create(data);
    else r = await deviceController.update(sel?.id || sel?._id, data);
    if (r.success) {
      setShowDevAdd(false); setShowDevEdit(false);
      fetchDevices();
      showToast(r.message);
    } else {
      showToast(r.errors?.join(', ') || r.message, 'error');
    }
    setSaving(false);
  };

  // ── Customer saved callback ──
  const handleCustomerSaved = (savedData, mode) => {
    setShowCustAdd(false);
    setShowCustEdit(false);
    fetchDevices(); // refresh list — linked device may have updated
    showToast(mode === 'add' ? 'Customer created successfully!' : 'Customer updated successfully!');
  };

  const handleDelete = async () => {
    const r = await deviceController.delete(sel.id || sel._id);
    if (r.success) { setShowDel(false); fetchDevices(); showToast(r.message); }
    else showToast(r.message, 'error');
  };

  const handlePaymentUpdate = async (updatedPayments) => {
    if (!sel?.id && !sel?._id) return;
    const updated = { ...sel.toJSON(), emiPlan: { ...sel.emiPlan, payments: updatedPayments } };
    await deviceController.update(sel.id || sel._id, updated);
    fetchDevices();
  };

  const paginated = filtered.slice((page - 1) * PER, page * PER);
  const totalPages = Math.ceil(filtered.length / PER);

  const stats = {
    total: devices.length,
    active: devices.filter(d => d.status === 'Active').length,
    nonCompliant: devices.filter(d => d.complianceStatus === 'Non-Compliant').length,
    lost: devices.filter(d => d.status === 'Lost').length,
    emiActive: devices.filter(d => d.emiPlan?.status === 'Active').length,
  };

  const SortIco = ({ f }) => sortField === f ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : null;
  const selInput = { padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff', cursor: 'pointer' };

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#dc2626' : '#16a34a',
          color: '#fff', borderRadius: 10, padding: '12px 20px',
          fontSize: 14, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,.2)',
          display: 'flex', alignItems: 'center', gap: 8, maxWidth: 380,
        }}>
          {toast.type === 'error' ? <XCircle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0 }}>All Devices</h1>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{devices.length} devices enrolled</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.btnSec}><Download size={15} /> Export</button>
          {/* Add Customer button */}
          <button style={{ ...S.btnSec, borderColor: '#7c3aed', color: '#7c3aed' }} onClick={() => setShowCustAdd(true)}>
            <User size={15} /> Add Customer
          </button>
          <button style={S.btnPri} onClick={() => setShowDevAdd(true)}><Plus size={15} /> Enroll Device</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 24 }}>
        {[
          ['Total Devices', stats.total, '#2563eb', <Smartphone size={20} color="#2563eb" />],
          ['Active', stats.active, '#16a34a', <CheckCircle size={20} color="#16a34a" />],
          ['Non-Compliant', stats.nonCompliant, '#dc2626', <XCircle size={20} color="#dc2626" />],
          ['Lost / Stolen', stats.lost, '#f59e0b', <AlertTriangle size={20} color="#f59e0b" />],
          ['EMI Active', stats.emiActive, '#7c3aed', <IndianRupee size={20} color="#7c3aed" />],
        ].map(([label, val, color, icon]) => (
          <div key={label} style={{ ...S.card, padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>{icon}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
          <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input placeholder="Search device, owner, phone, serial, key..." style={{ ...S.input, paddingLeft: 38 }} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {[
          [fStatus, setFStatus, ['All', 'Active', 'Inactive', 'Pending', 'Lost', 'Retired'], 'Status'],
          [fPlat, setFPlat, ['All', 'Android', 'iOS', 'Windows', 'macOS'], 'Platform'],
          [fComp, setFComp, ['All', 'Compliant', 'Non-Compliant', 'Unknown'], 'Compliance'],
        ].map(([val, set, opts, label]) => (
          <select key={label} style={selInput} value={val} onChange={e => set(e.target.value)}>
            {opts.map(o => <option key={o} value={o}>{o === 'All' ? `All ${label}` : o}</option>)}
          </select>
        ))}
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>Loading devices...</div>
        ) : paginated.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>No devices found.</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[['deviceKey', 'Device Key'], ['deviceName', 'Device'], ['owner', 'Owner / Customer'], ['platform', 'Platform'], ['status', 'Status'], ['complianceStatus', 'Compliance']].map(([f, l]) => (
                      <th key={f} style={S.th} onClick={() => handleSort(f)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{l}<SortIco f={f} /></div>
                      </th>
                    ))}
                    <th style={S.th}>EMI</th>
                    <th style={S.th}>Battery</th>
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(dev => {
                    const plan = dev.emiPlan;
                    const next = plan?.payments?.find(p => p.status !== 'Paid');
                    const batColor = dev.batteryLevel < 20 ? '#dc2626' : dev.batteryLevel < 50 ? '#f59e0b' : '#16a34a';
                    return (
                      <tr key={dev.id || dev.deviceKey}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={S.td}>
                          <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569', background: '#f1f5f9', padding: '3px 7px', borderRadius: 5, display: 'inline-block' }}>{dev.deviceKey}</div>
                        </td>
                        <td style={{ ...S.td, cursor: 'pointer' }} onClick={() => { setSel(dev); setShowView(true); }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 8, background: `${dev.getPlatformColor()}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: dev.getPlatformColor(), flexShrink: 0 }}>
                              <PlatformIcon platform={dev.platform} />
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{dev.deviceName}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>{dev.model} · {dev.serialNumber}</div>
                            </div>
                          </div>
                        </td>
                        {/* Owner / Customer column — shows customer data */}
                        <td style={S.td}>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{dev.owner?.fullName || '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{dev.owner?.phoneNumber || ''}</div>
                          {dev.owner?.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{dev.owner.email}</div>}
                          {/* Customer link badge if linked */}
                          {dev.linkedCustomer && (
                            <span style={{ ...S.badge('#7c3aed', '#ede9fe'), marginTop: 3, fontSize: 10 }}>
                              <User size={9} /> {dev.linkedCustomer.customerKey || 'Linked'}
                            </span>
                          )}
                        </td>
                        <td style={S.td}>
                          <Badge color={dev.getPlatformColor()} bg={`${dev.getPlatformColor()}15`}>
                            <PlatformIcon platform={dev.platform} size={11} />{dev.platform}
                          </Badge>
                        </td>
                        <td style={S.td}>
                          <Badge color={dev.getStatusColor()} bg={`${dev.getStatusColor()}15`}>{dev.status}</Badge>
                        </td>
                        <td style={S.td}>
                          <Badge color={dev.getComplianceColor()} bg={`${dev.getComplianceColor()}15`}>{dev.complianceStatus}</Badge>
                        </td>
                        <td style={S.td}>
                          {plan ? (
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>
                                {fmt(plan.emiAmount)}<span style={{ fontWeight: 400, color: '#94a3b8' }}>/{plan.tenureType?.slice(0, 3)}</span>
                              </div>
                              {next && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Due: {next.dueDate}</div>}
                              {!next && plan.payments?.length > 0 && <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>✓ Completed</div>}
                            </div>
                          ) : <span style={{ fontSize: 12, color: '#d1d5db' }}>—</span>}
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Battery size={13} color={batColor} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: batColor }}>{dev.batteryLevel}%</span>
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            <button style={S.iconBtn} title="View" onClick={() => { setSel(dev); setShowView(true); }}><Eye size={15} color="#64748b" /></button>
                            <button style={S.iconBtn} title="Edit Device" onClick={() => { setSel(dev); setShowDevEdit(true); }}><Edit2 size={15} color="#2563eb" /></button>
                            {/* Edit Customer button — opens customer form if owner data exists */}
                            <button style={S.iconBtn} title="Edit Customer" onClick={() => { setSel(dev); setShowCustEdit(true); }}>
                              <User size={15} color="#7c3aed" />
                            </button>
                            {plan && <button style={S.iconBtn} title="Payments" onClick={() => { setSel(dev); setShowPay(true); }}><IndianRupee size={15} color="#7c3aed" /></button>}
                            <button style={S.iconBtn} title="Delete" onClick={() => { setSel(dev); setShowDel(true); }}><Trash2 size={15} color="#dc2626" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Showing {(page - 1) * PER + 1}–{Math.min(page * PER, filtered.length)} of {filtered.length}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button style={S.btnSec} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</button>
                <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages || 1}</span>
                <button style={S.btnSec} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || totalPages === 0}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── MODALS ── */}

      {/* Device Add/Edit */}
      {(showDevAdd || showDevEdit) && (
        <DeviceFormModal
          mode={showDevAdd ? 'add' : 'edit'}
          device={showDevEdit ? sel : null}
          onClose={() => { setShowDevAdd(false); setShowDevEdit(false); }}
          onSave={handleDeviceSave}
          submitting={saving}
        />
      )}

      {/* Customer Add */}
      {showCustAdd && (
        <CustomerFormModal
          mode="add"
          customer={null}
          onClose={() => setShowCustAdd(false)}
          onSaved={handleCustomerSaved}
          submitting={saving}
          setSubmitting={setSaving}
        />
      )}

      {/* Customer Edit — pre-fill from device owner data */}
      {showCustEdit && sel && (
        <CustomerFormModal
          mode="edit"
          customer={
            // Map device owner → customer fields for pre-filling
            sel.linkedCustomer || {
              ...sel.owner,
              imei1: sel.imei1,
              imei2: sel.imei2,
              emiPlan: sel.emiPlan,
              customerKey: sel.linkedCustomerKey,
            }
          }
          onClose={() => setShowCustEdit(false)}
          onSaved={handleCustomerSaved}
          submitting={saving}
          setSubmitting={setSaving}
        />
      )}

      {/* View */}
      {showView && sel && (
        <ViewModal
          device={sel}
          onClose={() => setShowView(false)}
          onEdit={(d) => { setShowView(false); setSel(d); setShowDevEdit(true); }}
          onPayments={(d) => { setShowView(false); setSel(d); setShowPay(true); }}
        />
      )}

      {/* Payments */}
      {showPay && sel && (
        <PaymentHistoryModal
          device={sel}
          onClose={() => setShowPay(false)}
          onUpdate={handlePaymentUpdate}
        />
      )}

      {/* Delete */}
      {showDel && sel && (
        <div style={S.modal} onClick={() => setShowDel(false)}>
          <div style={{ ...S.mBox, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <h2 style={S.mTitle}>Remove Device</h2>
              <button style={S.iconBtn} onClick={() => setShowDel(false)}><X size={20} /></button>
            </div>
            <div style={{ padding: '28px 24px', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Trash2 size={28} color="#dc2626" />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Remove "{sel.deviceName}"?</p>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>This will unenroll the device from MDM. This action cannot be undone.</p>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', textAlign: 'left', fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{sel.deviceName}</div>
                <div style={{ color: '#64748b', marginTop: 2 }}>{sel.serialNumber} · {sel.platform}</div>
                {sel.owner?.fullName && <div style={{ color: '#64748b', marginTop: 2 }}>Owner: {sel.owner.fullName} · {sel.owner.phoneNumber}</div>}
              </div>
            </div>
            <div style={S.mFoot}>
              <button style={S.btnSec} onClick={() => setShowDel(false)}>Cancel</button>
              <button style={S.btnDng} onClick={handleDelete}>Remove Device</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllDevicesPage;